/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  HelpCircle, X, Loader2, Send, Rocket, ArrowLeft, LogIn, ArrowRight, RefreshCw,
  Mic, MicOff, Volume2, VolumeX,
} from 'lucide-react';
import {
  pageHelpApi, PageHelpCard, PageHelpLink, PageHelpPendingAction, PageHelpSource,
} from '@/api/pageHelp';
import { useAuthStore } from '@/store';
import { useHelpStore } from '@/store/helpStore';
import { usePageVisibility } from '@/hooks/usePageVisibility';
import { GETTING_STARTED_KEY, HELP_KEY_ROUTES, helpKeyForPath } from './helpKeys';
import {
  hasVoiceFor, primeSpeech, speak, speakableText, speechChunks, speechLocale, speechSupport,
  startDictation, stopSpeaking,
} from './speech';
import { MiniMarkdown } from './MiniMarkdown';
import { useChromeInsets } from './useChromeInsets';
import { dirForLanguage } from '@/i18n';

interface QaTurn {
  question: string;
  answer?: string;
  failed?: boolean;
  /** The question was about the caller's own records and they are not signed in. */
  needsSignIn?: boolean;
  /** Pages the answer offers to open, already filtered to ones this user may actually see. */
  links?: PageHelpLink[];
  /** Answered from the learned FAQ, so the user can ask for a fresh answer instead. */
  fromCache?: boolean;
  /** The text is still arriving: render what there is, but hold the footer actions back. */
  streaming?: boolean;
  /** A send this answer prepared, waiting to be allowed. Cleared once it goes out. */
  pendingAction?: PageHelpPendingAction | null;
  /** Pages a web-sourced answer was read from. Absent for an answer that came from the help text. */
  sources?: PageHelpSource[];
  /** What happened after the reader pressed Send. */
  actionResult?: string;
  actionSending?: boolean;
}

/**
 * The help drawer. Opened by <HelpButton />, which is rendered wherever a trigger is needed.
 *
 * The drawer answers one question — "what do I do on this screen?" — from a card stored per page and
 * per language, and offers a question box answered strictly from that same card. When a page has no
 * card it renders nothing and tells the buttons to hide, so no screen shows an empty help panel.
 *
 * Mounted once inside Layout for the signed-in portal, and once on each registration wizard, which
 * renders outside Layout for visitors who do not have an account yet.
 */
export const HelpPanel: React.FC<{
  /** Overrides the route-derived key — used by the registration wizards. */
  helpKey?: string;
}> = ({ helpKey: helpKeyProp }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  // The card and the answer follow the portal language the user picked.
  const lang = i18n.language || 'en';
  // Reading a card AND asking a question are open to everyone: anyone getting to know the product
  // should be able to. Signing in additionally lets the assistant answer from the user's OWN records,
  // so the anonymous hint below promises less than the signed-in one.
  const { isAuthenticated, isAdmin } = useAuthStore();
  // A suggested page is only offered when this user can open it — see visibleLinks below.
  const { isPageVisible } = usePageVisibility();
  // Opened by HelpButton, which is rendered separately in the desktop header, the mobile header and
  // the registration wizards — see helpStore for why the state is shared rather than local.
  const { isOpen, close: closePanel, setHasCard } = useHelpStore();
  // The drawer sits between the fixed header and footer and follows them when they are pinned or
  // unpinned, so it neither overlaps the chrome nor leaves a gap.
  const chrome = useChromeInsets();

  const routeKey = helpKeyProp || helpKeyForPath(location.pathname);
  const [activeKey, setActiveKey] = useState(routeKey);
  const [card, setCard] = useState<PageHelpCard | null>(null);
  const [loading, setLoading] = useState(false);
  const [missing, setMissing] = useState(false);

  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [turns, setTurns] = useState<QaTurn[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Set while the panel itself navigates, so following its own suggestion is not mistaken for the
  // user walking off to another screen.
  const followingLinkRef = useRef(false);

  // ---- voice -----------------------------------------------------------------------------------
  // Both directions run on the browser's own engines: no audio reaches our servers, and a question
  // asked out loud costs nothing. What the machine can do differs per browser and per language, so
  // every control here hides itself rather than failing when the engine is missing.
  const locale = speechLocale(lang);
  const [support] = useState(speechSupport);
  const [listening, setListening] = useState(false);
  const [micDenied, setMicDenied] = useState(false);
  const stopDictationRef = useRef<(() => void) | null>(null);

  // Voices arrive asynchronously in Chrome, so "no voice for this language" is only true once the list
  // has loaded — hence the listener rather than a single check.
  const [canSpeakLang, setCanSpeakLang] = useState(() => support.canSpeak && hasVoiceFor(locale));
  useEffect(() => {
    if (!support.canSpeak) return undefined;
    const check = () => setCanSpeakLang(hasVoiceFor(locale));
    check();
    window.speechSynthesis.addEventListener('voiceschanged', check);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', check);
  }, [locale, support.canSpeak]);

  // Reading answers aloud is a preference, not a mode: it survives reopening the panel.
  const [readAloud, setReadAloud] = useState(() => localStorage.getItem('help.readAloud') === '1');
  const toggleReadAloud = () => {
    setReadAloud((on) => {
      const next = !on;
      localStorage.setItem('help.readAloud', next ? '1' : '0');
      if (next) primeSpeech(); // this tap is the gesture iOS wants to see
      else stopSpeaking();
      return next;
    });
  };

  // How much of the streaming answer has already been spoken. Sentences are read as they complete, so
  // the answer is being read while it is still being written — the last piece waits for the end.
  const spokenRef = useRef(0);
  const speakPending = useCallback((text: string, isFinal: boolean) => {
    if (!readAloud || !canSpeakLang) return;
    const chunks = speechChunks(speakableText(text));
    // Mid-stream the final chunk may be half a sentence; leave it for the next delta.
    const upTo = isFinal ? chunks.length : chunks.length - 1;
    for (let i = spokenRef.current; i < upTo; i += 1) speak(chunks[i], locale);
    spokenRef.current = Math.max(spokenRef.current, Math.max(upTo, 0));
  }, [readAloud, canSpeakLang, locale]);

  const handleMic = () => {
    if (listening) {
      stopDictationRef.current?.();
      return;
    }
    stopSpeaking(); // never dictate over the panel's own voice
    setMicDenied(false);
    const stop = startDictation(locale, {
      onText: (text, isFinal) => {
        setQuestion(text);
        if (isFinal) stopDictationRef.current?.();
      },
      onEnd: () => { setListening(false); stopDictationRef.current = null; },
      onError: (error) => { if (error === 'not-allowed') setMicDenied(true); },
    });
    if (!stop) return;
    stopDictationRef.current = stop;
    setListening(true);
  };

  // Leaving the panel — closed, unmounted, or walked away from — must silence it.
  useEffect(() => {
    if (!isOpen) { stopSpeaking(); stopDictationRef.current?.(); }
  }, [isOpen]);
  useEffect(() => () => { stopSpeaking(); stopDictationRef.current?.(); }, []);

  // Keep the newest text in view while it streams in, so a growing answer does not write itself off
  // the bottom of the panel.
  const lastAnswer = turns.length ? turns[turns.length - 1].answer : undefined;
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [lastAnswer]);

  // Route change resets the panel to that page's card, so the drawer never shows stale help. The
  // conversation is kept when the move came from a suggested-page button: the user asked a question,
  // was offered a page, and pressed it — the answer they are acting on has to stay on screen.
  useEffect(() => {
    setActiveKey(routeKey);
    if (followingLinkRef.current) {
      followingLinkRef.current = false;
      return;
    }
    setTurns([]);
    setQuestion('');
  }, [routeKey]);

  const load = useCallback(async (key: string) => {
    setLoading(true);
    try {
      const data = await pageHelpApi.get(key, lang);
      setCard(data);
      setMissing(!data);
      setHasCard(!!data);
    } catch {
      // A failed probe must not break the page — just offer no help.
      setCard(null);
      setMissing(true);
      setHasCard(false);
    } finally {
      setLoading(false);
    }
  }, [lang, setHasCard]);

  // Probe once per key (and per language) so the button can hide itself when the page has no card.
  useEffect(() => { load(activeKey); }, [activeKey, load]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closePanel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, closePanel]);

  /**
   * A suggested page is only offered when this user can actually open it: an owner who has closed a
   * page in Settings, or who is not an admin, must not be handed a button that bounces them back.
   * Pages with no fixed address (one toll account, one owner's distribution settings) have no route
   * and are dropped as well — the answer still names them in words.
   */
  const visibleLinks = useCallback((links?: PageHelpLink[]): PageHelpLink[] => {
    if (!links?.length) return [];
    return links.filter((link) => {
      const route = HELP_KEY_ROUTES[link.helpKey];
      if (!route) return false;
      if (route.adminOnly && !isAdmin) return false;
      return isPageVisible(route.guiKey || link.helpKey);
    });
  }, [isAdmin, isPageVisible]);

  const runAsk = useCallback(async (q: string, forceRefresh: boolean) => {
    setAsking(true);

    // Only the last turn is ever being written to, so the updates below all target it by index.
    const patchLast = (patch: Partial<QaTurn> | ((turn: QaTurn) => Partial<QaTurn>)) =>
      setTurns((prev) => prev.map((turn, i) => (i === prev.length - 1
        ? { ...turn, ...(typeof patch === 'function' ? patch(turn) : patch) }
        : turn)));

    try {
      // The streamed answer is a preview: text appears while the model writes, and the final object
      // (links, flags) arrives at the end and is what gets rendered from then on. If streaming is
      // unavailable for any reason — an old proxy, a network that mangles SSE — the buffered endpoint
      // still answers, so the panel degrades to what it did before rather than to nothing.
      stopSpeaking();
      spokenRef.current = 0;
      let spoken = '';

      const res = await pageHelpApi.askStream(activeKey, q, lang, forceRefresh, {
        onDelta: (text) => {
          spoken += text;
          // Read what is finished while the rest is still being written.
          speakPending(spoken, false);
          patchLast((turn) => ({
            answer: (turn.answer || '') + text,
            streaming: true,
            failed: false,
          }));
        },
        onReset: () => {
          stopSpeaking();
          spokenRef.current = 0;
          spoken = '';
          patchLast({ answer: '', streaming: true });
        },
      }).catch(() => {
        patchLast({ answer: '', streaming: false });
        return pageHelpApi.ask(activeKey, q, lang, forceRefresh);
      });

      // Whatever is left unspoken — the closing sentence, or the whole answer when the stream fell
      // back to the buffered endpoint.
      speakPending(res.answer, true);

      patchLast({
        answer: res.answer,
        needsSignIn: res.needsSignIn,
        links: visibleLinks(res.links),
        fromCache: res.fromCache,
        sources: res.sources,
        pendingAction: res.pendingAction,
        streaming: false,
        failed: false,
      });
    } catch {
      setTurns((prev) => prev.map((turn, i) =>
        i === prev.length - 1
          ? { ...turn, answer: t('help.askFailed', 'The assistant is unavailable. Please try again.'), failed: true }
          : turn));
    } finally {
      setAsking(false);
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
      });
    }
  }, [activeKey, lang, t, visibleLinks, speakPending]);

  const handleAsk = async () => {
    const q = question.trim();
    if (!q || asking) return;
    // Inside the tap, while the browser still counts this as a user gesture — on iOS nothing may speak
    // afterwards otherwise, and the answer would arrive silently with the toggle on.
    if (readAloud) primeSpeech();
    setQuestion('');
    setTurns((prev) => [...prev, { question: q }]);
    await runAsk(q, false);
  };

  /** "That did not answer it" — ask the model again instead of reusing the learned answer. */
  const handleRetry = async (turn: QaTurn) => {
    if (asking) return;
    setTurns((prev) => [...prev, { question: turn.question }]);
    await runAsk(turn.question, true);
  };

  /**
   * Send what the assistant prepared. Deliberately a press: the panel has just shown the recipient, the
   * channels and the contact details, and this is the moment the reader takes responsibility for them.
   */
  const handleConfirmAction = async (turnIndex: number, action: PageHelpPendingAction) => {
    setTurns((prev) => prev.map((turn, i) =>
      (i === turnIndex ? { ...turn, actionSending: true } : turn)));
    try {
      const res = await pageHelpApi.confirmAction(action.id);
      setTurns((prev) => prev.map((turn, i) => (i === turnIndex
        ? {
          ...turn,
          actionSending: false,
          actionResult: res.message,
          // A send happens once: the card goes away whether it worked or not, and the message says which.
          pendingAction: null,
        }
        : turn)));
    } catch {
      setTurns((prev) => prev.map((turn, i) => (i === turnIndex
        ? {
          ...turn,
          actionSending: false,
          actionResult: t('help.sendFailed', 'It could not be sent. Try from the booking itself.'),
          pendingAction: null,
        }
        : turn)));
    }
  };

  /**
   * Open a suggested page and switch the panel to that page's card, keeping the conversation. The
   * user asked "how do I load vehicles", picked a page, and should land there already reading how.
   */
  const handleGoTo = (link: PageHelpLink, question: string) => {
    const route = HELP_KEY_ROUTES[link.helpKey];
    if (!route) return;
    followingLinkRef.current = true;
    // "Bookings with John Doe" opens the list already searching for him — every page the server is
    // willing to send a term to reads ?search= from the URL.
    const path = link.search
      ? `${route.path}?${new URLSearchParams({ search: link.search }).toString()}`
      : route.path;
    navigate(path);
    setActiveKey(link.helpKey);
    // Which suggestion was actually worth opening is the only honest signal about whether the ranking
    // was right, and it is the one the next asker benefits from. Failing to record it changes nothing
    // for this user, so it never blocks the navigation.
    void pageHelpApi.recordNavClick(question, lang, link.helpKey).catch(() => undefined);
  };

  // Nothing to show for this page — the store has already told every button to hide itself.
  if (missing && !card) return null;

  const showBackToPage = activeKey !== routeKey;
  // Persian and Urdu need the drawer laid out right-to-left; the card body follows the language the
  // server actually served, so an English fallback card is not right-aligned inside an RTL drawer.
  const uiDir = dirForLanguage(lang);
  const cardDir = dirForLanguage(card?.language || lang);

  return (
    <>
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-[1100] bg-black/30"
            onClick={() => closePanel()}
            aria-hidden="true"
          />
          {/*
            Two things keep the drawer clear of the app chrome. The inline top/bottom insets size it to
            the space between the fixed header and footer, measured live so pinning either one moves it.
            The z-index then sits above that chrome (plain CSS at 1001–1006): at the original z-[61] the
            footer painted over the ask box, so the input was present but invisible.
          */}
          <aside
            role="dialog"
            aria-modal="true"
            aria-label={t('help.button', 'Help')}
            dir={uiDir}
            style={{ top: chrome.top, bottom: chrome.bottom }}
            className="fixed right-0 z-[1101] flex w-full max-w-md flex-col bg-white shadow-2xl"
          >
            {/* header */}
            <div className="flex items-start justify-between gap-2 border-b px-4 py-3">
              <div className="min-w-0" dir={cardDir}>
                <div className="flex items-center gap-2">
                  <HelpCircle className="h-4 w-4 flex-shrink-0 text-primary-600" />
                  <h2 className="truncate text-sm font-semibold text-gray-900">
                    {card?.title || t('help.button', 'Help')}
                  </h2>
                </div>
                {card?.summary && (
                  <p className="mt-1 text-xs leading-snug text-gray-500">{card.summary}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => closePanel()}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                aria-label={t('common.close', 'Close')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* body */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3">
              {loading && (
                <div className="flex items-center gap-2 py-6 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('help.loading', 'Loading…')}
                </div>
              )}

              {!loading && card && (
                <div dir={cardDir}><MiniMarkdown markdown={card.body} /></div>
              )}

              {!loading && card?.isMachineTranslated && (
                <p className="mt-3 text-[11px] italic text-gray-400">
                  {t('help.machineTranslated', 'This text was translated automatically.')}
                </p>
              )}

              {/* navigation between this page's card and the getting-started card */}
              <div className="mt-4 flex flex-wrap gap-2 border-t pt-3">
                {showBackToPage ? (
                  <button
                    type="button"
                    onClick={() => { setActiveKey(routeKey); setTurns([]); }}
                    className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    {t('help.backToPage', 'Back to this page')}
                  </button>
                ) : (
                  activeKey !== GETTING_STARTED_KEY && (
                    <button
                      type="button"
                      onClick={() => { setActiveKey(GETTING_STARTED_KEY); setTurns([]); }}
                      className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <Rocket className="h-3.5 w-3.5 text-primary-600" />
                      {t('help.gettingStarted', 'New here? Getting started')}
                    </button>
                  )
                )}
              </div>

              {/* question/answer thread */}
              {turns.length > 0 && (
                <div className="mt-4 space-y-3 border-t pt-3">
                  {turns.map((turn, i) => (
                    <div key={i} className="space-y-1.5">
                      <div className="rounded-md bg-primary-50 px-3 py-2 text-[13px] text-gray-800">
                        {turn.question}
                      </div>
                      {turn.answer ? (
                        <div
                          className={`rounded-md px-3 py-2 text-[13px] ${
                            turn.failed ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-800'
                          }`}
                        >
                          <MiniMarkdown markdown={turn.answer} />
                          {/* Still being written — the caret is what tells a reader to wait rather than
                              act on half a sentence. */}
                          {turn.streaming && (
                            <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-gray-400 align-text-bottom" />
                          )}
                          {/* Where a web-sourced answer came from. Shown, not optional: the answer was
                              built from pages we do not control, and the reader is entitled to check
                              them — a published commission can change the day after it is read. */}
                          {!turn.streaming && turn.sources && turn.sources.length > 0 && (
                            <div className="mt-2 border-t border-gray-200 pt-1.5">
                              <div className="text-[11px] font-medium text-gray-500">
                                {t('help.sources', 'Read from')}
                              </div>
                              <ul className="mt-0.5 space-y-0.5">
                                {turn.sources.map((s) => (
                                  <li key={s.url}>
                                    <a
                                      href={s.url}
                                      target="_blank"
                                      rel="noopener noreferrer nofollow"
                                      className="text-[12px] text-primary-700 underline hover:text-primary-800 break-all"
                                    >
                                      {s.title || s.url}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {turn.needsSignIn && (
                            <a
                              href="/login"
                              className="mt-1.5 inline-flex items-center gap-1 text-[13px] font-medium text-primary-700 hover:text-primary-800"
                            >
                              <LogIn className="h-3.5 w-3.5" />
                              {t('help.signIn', 'Sign in')}
                            </a>
                          )}

                          {/* A send waiting to be allowed. Everything the reader needs to judge it is on
                              screen — who, where, by which channel — because that is what they are
                              approving. Nothing has left the building until this button is pressed. */}
                          {turn.pendingAction && (
                            <div className="mt-2.5 rounded-md border border-amber-300 bg-amber-50 p-2.5">
                              <p className="text-[12px] font-medium text-amber-900">
                                {turn.pendingAction.kind === 'card_link'
                                  ? t('help.confirmCardLink', 'Send the card registration link?')
                                  : t('help.confirmAgreement', 'Send the rental agreement?')}
                              </p>
                              <dl className="mt-1.5 space-y-0.5 text-[12px] text-amber-900">
                                <div className="flex gap-1.5">
                                  <dt className="text-amber-700">{t('help.confirmTo', 'To')}:</dt>
                                  <dd className="font-medium">{turn.pendingAction.driverName}</dd>
                                </div>
                                {turn.pendingAction.phone && (
                                  <div className="flex gap-1.5">
                                    <dt className="text-amber-700">{t('help.confirmPhone', 'Phone')}:</dt>
                                    <dd>{turn.pendingAction.phone}</dd>
                                  </div>
                                )}
                                {turn.pendingAction.email && (
                                  <div className="flex gap-1.5">
                                    <dt className="text-amber-700">{t('help.confirmEmail', 'E-mail')}:</dt>
                                    <dd className="break-all">{turn.pendingAction.email}</dd>
                                  </div>
                                )}
                                <div className="flex gap-1.5">
                                  <dt className="text-amber-700">{t('help.confirmBooking', 'Booking')}:</dt>
                                  <dd>{turn.pendingAction.vehicle} · {turn.pendingAction.dates}</dd>
                                </div>
                                <div className="flex gap-1.5">
                                  <dt className="text-amber-700">{t('help.confirmVia', 'Via')}:</dt>
                                  <dd className="font-medium">
                                    {turn.pendingAction.channel === 'both'
                                      ? t('help.channelBoth', 'SMS and e-mail')
                                      : turn.pendingAction.channel === 'sms'
                                        ? t('help.channelSms', 'SMS')
                                        : turn.pendingAction.channel === 'email'
                                          ? t('help.channelEmail', 'E-mail')
                                          : t('help.channelDefault', 'your usual delivery method')}
                                  </dd>
                                </div>
                              </dl>
                              <button
                                type="button"
                                onClick={() => handleConfirmAction(i, turn.pendingAction!)}
                                disabled={turn.actionSending}
                                className="mt-2 inline-flex min-h-[40px] items-center gap-1.5 rounded-md bg-amber-600 px-3 py-2 text-[13px] font-medium text-white hover:bg-amber-700 disabled:opacity-60"
                              >
                                {turn.actionSending
                                  ? <Loader2 className="h-4 w-4 animate-spin" />
                                  : <Send className="h-4 w-4" />}
                                {t('help.confirmSend', 'Send it')}
                              </button>
                            </div>
                          )}

                          {turn.actionResult && (
                            <p className="mt-2 text-[12px] text-gray-700">{turn.actionResult}</p>
                          )}

                          {/* Where to do it. One button per page that can do what was asked. Held back
                              while the text streams: the links arrive with the finished answer. */}
                          {!turn.streaming && !!turn.links?.length && (
                            <div className="mt-2.5 border-t border-gray-200 pt-2">
                              <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                                {turn.links.length > 1
                                  ? t('help.openOneOfThese', 'Open one of these pages')
                                  : t('help.openThisPage', 'Open this page')}
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {turn.links.map((link) => (
                                  <button
                                    key={link.helpKey}
                                    type="button"
                                    onClick={() => handleGoTo(link, turn.question)}
                                    className="inline-flex min-h-[40px] items-center gap-1 rounded-md border border-primary-200 bg-white px-3 py-2 text-xs font-medium text-primary-700 hover:bg-primary-50 sm:min-h-0 sm:px-2.5 sm:py-1.5"
                                  >
                                    {link.title}
                                    <ArrowRight className="h-3.5 w-3.5" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Hear it again — the one control that is useful even with reading off,
                              for an answer that went by while the reader was looking elsewhere. */}
                          {canSpeakLang && !turn.streaming && !turn.failed && turn.answer && (
                            <button
                              type="button"
                              onClick={() => {
                                stopSpeaking();
                                speechChunks(speakableText(turn.answer || ''))
                                  .forEach((chunk) => speak(chunk, locale));
                              }}
                              className="mt-2 mr-3 inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700"
                              title={t('help.speakAnswer', 'Read this answer aloud')}
                            >
                              <Volume2 className="h-3 w-3" />
                              {t('help.speakAnswer', 'Read this answer aloud')}
                            </button>
                          )}

                          {/* A reused answer may be close but not exact — one press asks afresh. */}
                          {turn.fromCache && !asking && (
                            <button
                              type="button"
                              onClick={() => handleRetry(turn)}
                              className="mt-2 inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700"
                            >
                              <RefreshCw className="h-3 w-3" />
                              {t('help.answerAgain', 'Not what you meant? Answer again')}
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-500">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          {t('help.thinking', 'Looking it up…')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ask box — open to everyone, including visitors with no account */}
            <div className="border-t px-4 py-3">
              <div className="flex items-end gap-2">
                <textarea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAsk(); }
                  }}
                  rows={2}
                  maxLength={500}
                  placeholder={t('help.askPlaceholderApp', 'Ask anything about the portal…')}
                  /* 16px on phones: iOS zooms the whole page when a focused field is smaller, and the
                     drawer never zooms back out. sm: restores the compact desktop size. */
                  className="flex-1 resize-none rounded-md border border-gray-300 px-3 py-2 text-[16px] focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 sm:text-[13px]"
                />
                {/* Dictation. Hidden entirely where the browser has no engine (Firefox) rather than
                    offered and then failing. */}
                {support.canListen && (
                  <button
                    type="button"
                    onClick={handleMic}
                    disabled={asking}
                    /* 44px on touch, the size a thumb actually hits; the desktop keeps its 36px. */
                    className={`inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md border disabled:opacity-50 sm:h-9 sm:w-9 ${
                      listening
                        ? 'animate-pulse border-red-300 bg-red-50 text-red-600'
                        : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                    aria-label={listening
                      ? t('help.voiceStop', 'Stop dictating')
                      : t('help.voiceStart', 'Ask by voice')}
                    title={listening
                      ? t('help.voiceStop', 'Stop dictating')
                      : t('help.voiceStart', 'Ask by voice')}
                  >
                    {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleAsk}
                  disabled={asking || question.trim().length === 0}
                  className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 sm:h-9 sm:w-9"
                  aria-label={t('help.ask', 'Ask')}
                >
                  {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>

              {micDenied && (
                <p className="mt-1.5 text-[11px] text-red-600">
                  {t('help.voiceDenied',
                    'The browser is blocking the microphone. Allow it in the address bar to ask by voice.')}
                </p>
              )}
              <div className="mt-1.5 flex items-start justify-between gap-3">
                <p className="text-[11px] text-gray-400">
                  {isAuthenticated
                    ? t('help.askHintApp',
                        'Ask about any screen, not just this one — the answer will offer to take you there.')
                    : t('help.askHintAnonymous',
                        'Ask anything about how the portal works. Your own bookings and charges are visible only after you sign in.')}
                </p>

                {/* Reading answers aloud. Offered only when this machine actually has a voice for the
                    portal's language — an Armenian answer read by an English voice helps nobody. */}
                {canSpeakLang && (
                  <button
                    type="button"
                    onClick={toggleReadAloud}
                    /* A text button still needs a thumb-sized hit area on a phone. */
                    className={`-my-2 inline-flex min-h-[44px] flex-shrink-0 items-center gap-1 px-1 text-[11px] sm:min-h-0 sm:py-0 ${
                      readAloud ? 'text-primary-700' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    aria-pressed={readAloud}
                    title={t('help.readAloudHint', 'Read answers aloud')}
                  >
                    {readAloud ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
                    {t('help.readAloud', 'Read aloud')}
                  </button>
                )}
              </div>
            </div>
          </aside>
        </>
      )}
    </>
  );
};

export default HelpPanel;
